import axios from '@/lib/axios'
import { useQuery } from '@tanstack/vue-query'
import { useDebounceFn } from '@vueuse/core'
import { chain, orderBy } from 'lodash'
import { reactive, ref, watch } from 'vue'

import { CustomAxiosRequestConfig } from '@/interfaces/customAxiosRequestConfig'
import { PaginatedResponse } from '@/interfaces/paginatedResponse'

import { Chain } from '@/internal/modules/providers/interfaces/Chain'

import { Brand } from '../../../../interfaces/Brand'

interface Props {
	immediate?: boolean
	queryKey?: string
	errorHandling?: boolean
}

interface Params {
	page?: number
	q?: string
	filters?: Filters
}

interface Filters {
	orderBy?: string
	orderDirection?: string
	active?: number | string
	chain?: Chain
}

const SearchBrands = async (
	params: Params,
	errorHandling = true
): Promise<PaginatedResponse<Brand>> => {
	const { data } = await axios.get(
		`/api/internal/brands/search?q=${params.q}&page=${params.page}`,
		{
			errorHandling: errorHandling,
			params:
				params.filters ?
					{
						orderBy: params.filters.orderBy,
						orderDirection: params.filters.orderDirection,
						active:
							params.filters.active !== 'Todos' ?
								params.filters.active
							:	undefined,
						chain: params.filters.chain?.id,
					}
				:	undefined,
		} as CustomAxiosRequestConfig
	)

	return data
}

const useSearchBrands = (props: Props) => {
	const { immediate = true, errorHandling } = props
	const page = ref<number | string>(1)
	const query = ref<string>('')

	const filters = reactive<Filters>({
		orderBy: 'name',
		orderDirection: 'asc',
		active: undefined,
		chain: undefined,
	})

	const { data, isLoading, refetch } = useQuery(
		['brands', props.queryKey],
		() =>
			SearchBrands(
				{
					page: Number(page.value),
					q: query.value,
					filters,
				},
				errorHandling
			),
		{
			enabled: immediate,
		}
	)

	const setQuery = (q: string) => {
		query.value = q
		page.value = 1
	}

	const debouncedSetQuery = useDebounceFn((e) => {
		setQuery(e)
	}, 500)

	watch(query, () => {
		if (immediate) {
			refetch()
		}
	})

	watch(filters, () => {
		if (immediate) {
			refetch()
		}
	})

	return {
		data,
		isLoading,
		refetch,
		page,
		setQuery,
		debouncedSetQuery,
		query,
		filters,
	}
}

export default useSearchBrands
